import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../components/Config/api.service';
import PayslipStateBadge from './PayslipStateBadge';
import PayslipTemplate from './PayslipTemplate';
import ReopenClosedPayslipModal from './ReopenClosedPayslipModal';
import { canEditHrData } from '../../../components/Auth/permissions';


const BulletinTab = ({ Report, isLoading, payslipData, handleCalculate, handleRecalculate, handleSendPayslip, employeeId, handleCancelPayslip, isMonthClosed = false, showResetPendingOnClosedMonth = false, onPdfLoaded }) => {
    const { t } = useTranslation();
    const [isPrinting, setIsPrinting] = useState(false);
    const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
    const canEdit = canEditHrData();

    const handlePrintPDF = async () => {
        if (!Report?.id || isPrinting) return;

        setIsPrinting(true);
        try {
            const response = await apiService.getBlobFile(`/payslip/pdf/${Report.id}`);
            if (response && response.data) {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `bulletin_${Report.id}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Erreur lors du téléchargement du PDF:', error);
            alert(t('payslipBulletin.downloadError'));
        } finally {
            setIsPrinting(false);
        }
    };

    const printButton = (
        <button
            className="bg-[--bg-color] text-[--color-primary] btn rounded-full text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!Report?.id || isPrinting}
            onClick={handlePrintPDF}
        >
            {isPrinting ? (
                <span className="loading loading-spinner loading-xs mr-1"></span>
            ) : (
                <span className="mr-1">🖨️</span>
            )}
            {isPrinting ? t('payslipBulletin.downloading') : t('payslipBulletin.print')}
        </button>
    );

    const showResetPending = !isMonthClosed || showResetPendingOnClosedMonth;

    const resetPendingButton = showResetPending && (
        <button
            className="hover:bg-[--color-primary] hover:text-white btn btn-outline  rounded-full text-[--color-primary] text-sm flex items-center"
            // Mois clôturé : action critique, passage obligé par la modale d'avertissement.
            onClick={isMonthClosed ? () => setIsReopenModalOpen(true) : handleRecalculate}
            disabled={!employeeId}
        >
            {t('payslipBulletin.backToPending')}
        </button>
    );

    const handleConfirmReopen = () => {
        setIsReopenModalOpen(false);
        handleRecalculate();
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-[600px] p-6">
            <div className="mb-4 flex flex-col md:flex-row justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold flex items-center">
                        {t('payslipBulletin.payslipOf', { employee: Report?.Employee || t('payslipBulletin.selectedEmployee') })}
                        {Report?.State && (
                            <span className="ml-3">
                                <PayslipStateBadge state={Report.State} />
                            </span>
                        )}
                    </h1>
                </div>
                <div className="flex gap-2 mt-3 md:mt-0">
                    {!canEdit ? (
                        // Consultation seule : téléchargement du bulletin une fois clôturé.
                        Report?.State === 'done' && printButton
                    ) : Report.State === 'cancel' || Report.State === 'done' ? (
                        // Mois clôturé : « Remettre en attente » masqué sauf réglage société ;
                        // « Imprimer » proposé sur les bulletins clôturés (jamais sur un annulé).
                        <>
                            {resetPendingButton}
                            {isMonthClosed && Report.State === 'done' && printButton}
                        </>
                    ) : payslipData ? (
                        <>
                            <button
                                className="bg-[--color-primary] hover:bg-[--bg-hover] btn rounded-full text-white text-sm flex items-center"
                                onClick={handleRecalculate}
                                disabled={!Report?.id}
                            >
                                <span className="mr-1">↻</span> {t('payslipBulletin.recalculate')}
                            </button>
                            {printButton}
                            <button
                                className="bg-red-50 text-red-700 border border-red-300 hover:bg-red-100 btn rounded-full text-sm flex items-center"
                                onClick={handleCancelPayslip}
                                disabled={!Report?.id}
                            >
                                {t('basePrimeForm.cancel')}
                            </button>
                        </>
                    ) : (
                        <button
                            className="bg-[--color-primary] btn rounded-full text-white text-sm flex items-center"
                            onClick={handleCalculate}
                            disabled={!employeeId}
                        >
                            <span className="mr-1">+</span> {t('payslipBulletin.calculate')}
                        </button>
                    )}

                </div>
            </div>

            <hr className="py-1 mb-4" />

            {/* Skeleton loader */}

            {/* PDF Viewer */}
            {isLoading ? (
                <div className={`h-[calc(100vh-200px)] overflow-y-auto `}>
                    <div className="max-w-3xl p-4 rounded-lg bg-base-200 mx-auto space-y-4 animate-pulse">
                        <div className="animate-pulse bg-base-300 h-6 w-1/4"></div>
                        <div className="flex justify-end space-x-4">
                            <div className="animate-pulse bg-base-300 h-4 w-1/4"></div>
                            <div className="animate-pulse bg-base-300 h-4 w-1/6"></div>
                            <div className="animate-pulse bg-base-300 h-4 w-1/5"></div>
                        </div>
                        <div className="animate-pulse bg-base-300 h-[2px] w-full"></div>
                        <div className="space-y-2">
                            <div className="animate-pulse bg-base-300 h-[100vh] w-4/4"></div>
                        </div>
                        <div className="animate-pulse bg-base-300 h-4 w-1/3 mt-8"></div>
                        <p className="text-center text-sm text-gray-500 mt-6">{t('payslipBulletin.loading')}</p>
                    </div>
                </div>
            ) : payslipData && (
                <div className={`max-h-[75vh] md:h-[calc(100vh-200px)] overflow-y-auto bg-gray-100 p-4 rounded`}>
                    <PayslipTemplate data={payslipData} />
                </div>
            )}

            {/* Cas aucun PDF */}
            {!isLoading && !payslipData && (
                <p className="text-center text-gray-500">{t('payslipBulletin.noPayslip')}</p>
            )}

            <ReopenClosedPayslipModal
                isOpen={isReopenModalOpen}
                onClose={() => setIsReopenModalOpen(false)}
                onConfirm={handleConfirmReopen}
                employee={Report?.Employee}
            />
        </div>
    );
}

export default BulletinTab;